/**
 * Configuración → «Envío»: franja horaria, ritmo, límite diario, fatiga, teléfonos sin +54 e idioma
 * de las plantillas. No hay grupo de control: WhatsApp oficial le escribe a todos los que pasan el
 * chequeo (el backend rechaza un control con 422). Los links acortados (Link Shortening) se prenden
 * en «Conexión (técnico)»: necesitan un dominio en la consola de Twilio (`TarjetaLinkShortening`).
 *
 * Este archivo también exporta `useFormularioConfig`, `CampoNumero` y `BotoneraGuardar`, que usan
 * las secciones de Bajas y de Saldo, y la tarjeta de links acortados. Viven acá y no en `ConfiguracionOficialPage` para que las
 * secciones no importen a la página que las importa a ellas (un ciclo de imports).
 *
 * Cómo guarda un formulario de config (igual en las tres secciones):
 * - Cada sección edita SU copia de la config y manda SÓLO lo que cambió (`diffConfig`): dos
 *   personas cambiando secciones distintas no se pisan una a la otra.
 * - Valida antes con los mismos rangos que el backend (`validarConfigLocal`); si igual el backend
 *   rechaza (422 BAD_INPUT), se muestra su mensaje.
 * - Sin cuenta real también se guarda: el backend crea el documento (`asegurar`, SPEC-backend §9.1).
 * - Después de guardar, `recargar()` del contexto: la verdad es lo que devuelve el backend.
 */

import { useEffect, useId, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, RotateCcw, Save } from "lucide-react";
import type { ConfigCuenta } from "../../types";
import { oficialApi } from "../../api";
import { useCuentaOficial } from "../../contexto";
import { diffConfig, textoLimiteMeta, validarConfigLocal, validarFranjasLocal } from "../../logic/config";
import { sinNumeroEnTwilio } from "../../logic/resumen";
import { textoErrorConfig } from "../../logic/textos";
import { leerError, leerRespuesta, textoError } from "../../logic/respuesta";
import { EditorFranjas } from "../../components/EditorFranjas";
import { Tarjeta } from "../../components/Tarjeta";
import { num } from "../../../campana-nueva/utils/format";
import { LimiteWhatsAppManager } from "./LimiteWhatsAppManager";

// ─────────────────────────────────────────────────────────────────────────────────────────────
// Piezas compartidas de los formularios de config
// ─────────────────────────────────────────────────────────────────────────────────────────────

export function useFormularioConfig() {
  const { config, recargar } = useCuentaOficial();
  const [editado, setEditado] = useState<ConfigCuenta | null>(config);
  const [errorServidor, setErrorServidor] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);

  // Cuando la config vuelve del backend, el formulario se «rebasa» sobre ella: lo que el operador
  // cambió y todavía no guardó se conserva, y todo lo demás toma el valor nuevo. Pisar el
  // formulario entero borraba lo tipeado cuando otra tarjeta guardaba algo (el opt-out, en Bajas);
  // conservarlo entero hacía peor: al guardar, el diff revertía lo que había guardado la otra.
  const configAnterior = useRef<ConfigCuenta | null>(config);
  useEffect(() => {
    const antes = configAnterior.current;
    configAnterior.current = config;
    setEditado((prev) => {
      if (!config) return config;
      if (!prev || !antes) return config;
      const propios = diffConfig(antes, prev);
      return { ...config, ...propios };
    });
  }, [config]);

  useEffect(() => {
    if (!guardado) return;
    const t = setTimeout(() => setGuardado(false), 2000);
    return () => clearTimeout(t);
  }, [guardado]);

  const cambios: Partial<ConfigCuenta> = config && editado ? diffConfig(config, editado) : {};
  const hayCambios = Object.keys(cambios).length > 0;
  const errores = validarConfigLocal(cambios);
  const hayErrores = Object.keys(errores).length > 0;

  const set = <K extends keyof ConfigCuenta>(k: K, v: ConfigCuenta[K]) => {
    setEditado((e) => (e ? { ...e, [k]: v } : e));
    setGuardado(false);
    setErrorServidor("");
  };

  const guardar = async () => {
    if (!hayCambios || hayErrores || guardando) return;
    setGuardando(true);
    setErrorServidor("");
    try {
      const l = leerRespuesta(await oficialApi.actualizarConfig(cambios));
      // sin dominio de acortado, prender Link Shortening vuelve 422 y no se guarda nada (§B.6.1)
      if (!l.ok) { setErrorServidor(textoErrorConfig(l) || textoError(l)); return; }
      await recargar();
      setGuardado(true);
    } catch (e) {
      setErrorServidor(textoError(leerError(e)));
    } finally {
      setGuardando(false);
    }
  };

  const descartar = () => { setEditado(config); setErrorServidor(""); };

  return { config, editado, set, cambios, hayCambios, errores, hayErrores, guardar, descartar, guardando, guardado, errorServidor };
}

export function BotoneraGuardar({ f }: { f: ReturnType<typeof useFormularioConfig> }) {
  return (
    <div className="flex items-center gap-3 flex-wrap pt-2">
      <button type="button" onClick={() => void f.guardar()} disabled={!f.hayCambios || f.hayErrores || f.guardando}
        className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed">
        {f.guardando ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Guardar cambios
      </button>
      <button type="button" onClick={f.descartar} disabled={!f.hayCambios || f.guardando}
        className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">
        <RotateCcw size={15} /> Descartar
      </button>
      {f.guardado && <span className="inline-flex items-center gap-1 text-sm text-emerald-700"><CheckCircle2 size={15} /> Guardado</span>}
      {f.hayCambios && f.hayErrores && <span className="text-xs text-red-600">Corregí lo que está en rojo para guardar.</span>}
      {f.errorServidor && <p role="alert" className="w-full text-sm text-red-600">{f.errorServidor}</p>}
    </div>
  );
}

/**
 * Input numérico que no inventa ceros: vacío es `null` si el campo lo admite (sin tope, límite de
 * Meta) y NaN si no (el validador lo marca). Nunca un 0 que el operador no escribió.
 */
export function CampoNumero({ label, value, onChange, nulable = false, min, max, step = 1, sufijo, error, ayuda, placeholder, disabled }: {
  label: string; value: number | null; onChange: (n: number | null) => void; nulable?: boolean;
  min?: number; max?: number; step?: number; sufijo?: string; error?: string; ayuda?: string; placeholder?: string; disabled?: boolean;
}) {
  const id = useId();
  const mostrado = value === null || value === undefined || !Number.isFinite(value) ? "" : String(value);
  return (
    <div>
      <label htmlFor={id} className="text-sm font-medium text-slate-700">{label}</label>
      <div className="mt-1 flex items-center gap-2">
        <input
          id={id}
          type="number"
          inputMode="decimal"
          value={mostrado}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={!!error || undefined}
          onChange={(e) => {
            const v = e.target.value;
            if (v.trim() === "") onChange(nulable ? null : NaN);
            else onChange(Number(v));
          }}
          className={`w-36 rounded-xl border px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:bg-slate-50 ${error ? "border-red-300" : "border-slate-200"}`}
        />
        {sufijo && <span className="text-sm text-slate-500">{sufijo}</span>}
      </div>
      {error ? <p className="text-xs text-red-600 mt-1">{error}</p> : ayuda ? <p className="text-xs text-slate-400 mt-1">{ayuda}</p> : null}
    </div>
  );
}

/**
 * La regla de fatiga de la marca (29/10/2026): «Como mucho [K] mensaje(s) cada [N] días por persona, sumando WhatsApp
 * oficial y por QR», y la casilla de los mails. Vale para las campañas del oficial y para las de Reactivación por las
 * líneas: una campaña de Reactivación puede pedir más días, nunca menos. Sin `fatigaMaximo` (un backend anterior) se
 * ofrecen sólo los días, como antes.
 */
export function ReglaFatiga({ f }: { f: ReturnType<typeof useFormularioConfig> }) {
  const idK = useId();
  const idN = useId();
  const e = f.editado;
  if (!e) return null;
  const conMaximo = e.fatigaMaximo !== undefined;
  const k = conMaximo ? e.fatigaMaximo : 1;
  const numero = (v: string) => (v.trim() === "" ? NaN : Number(v));
  const caja = (error?: string) =>
    `w-20 rounded-xl border px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-violet-500 ${error ? "border-red-300" : "border-slate-200"}`;
  const mostrar = (n: number | undefined) => (n === undefined || !Number.isFinite(n) ? "" : String(n));
  return (
    <fieldset data-testid="regla-fatiga">
      <legend className="text-sm font-medium text-slate-700">Cuántos mensajes, como mucho, a cada persona</legend>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-700">
        <span>Como mucho</span>
        {conMaximo ? (
          <>
            <label htmlFor={idK} className="sr-only">Cuántos mensajes como mucho</label>
            <input id={idK} type="number" inputMode="numeric" min={1} max={10} step={1} value={mostrar(e.fatigaMaximo)}
              aria-invalid={!!f.errores.fatigaMaximo || undefined} onChange={(ev) => f.set("fatigaMaximo", numero(ev.target.value))} className={caja(f.errores.fatigaMaximo)} />
          </>
        ) : (
          <span className="font-medium">1</span>
        )}
        <span>{k === 1 ? "mensaje" : "mensajes"} cada</span>
        <label htmlFor={idN} className="sr-only">Cada cuántos días</label>
        <input id={idN} type="number" inputMode="numeric" min={0} max={30} step={1} value={mostrar(e.fatigaDias)}
          aria-invalid={!!f.errores.fatigaDias || undefined} onChange={(ev) => f.set("fatigaDias", numero(ev.target.value))} className={caja(f.errores.fatigaDias)} />
        <span>días por persona, sumando WhatsApp oficial y por QR</span>
      </div>
      {f.errores.fatigaMaximo && <p className="text-xs text-red-600 mt-1">{f.errores.fatigaMaximo}</p>}
      {f.errores.fatigaDias && <p className="text-xs text-red-600 mt-1">{f.errores.fatigaDias}</p>}
      <p className="text-xs text-slate-400 mt-1">
        Cuenta el marketing del oficial y lo que mandan las líneas (Reactivación por QR). Una campaña de Reactivación puede pedir más días,
        nunca menos. 0 días = sin límite.
      </p>
      {e.fatigaContarMails !== undefined && (
        <label className="mt-2 flex items-start gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={e.fatigaContarMails} onChange={(ev) => f.set("fatigaContarMails", ev.target.checked)}
            className="mt-0.5 rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
          <span>
            Contar también los mails
            <span className="block text-xs text-slate-400">
              Suma los que se pueden saber por persona: los de las casillas de Reactivación y los de Snov de un envío armado en Activa. Una
              campaña de Snov armada por fuera todavía no se puede contar.
            </span>
          </span>
        </label>
      )}
    </fieldset>
  );
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// La sección
// ─────────────────────────────────────────────────────────────────────────────────────────────

export default function SeccionEnvio() {
  const { cuenta, recargar } = useCuentaOficial();
  const f = useFormularioConfig();
  const e = f.editado;
  if (!e || !f.config || !cuenta) return null;

  // En twilio sin número (o con el +5491100000000 de una cuenta que sigue en modo simulado), el sender
  // que quedó es el del simulador: su límite no es de ningún número real.
  const sinNumero = sinNumeroEnTwilio(cuenta.modoTransporte, cuenta);
  const limiteMeta = textoLimiteMeta(cuenta, sinNumero);
  const cupoManual = e.cupoManual24h !== null;
  const ritmoBajado = cuenta.ritmoActualMps < f.config.ritmoMps;
  const erroresFranjas = validarFranjasLocal(e.franjas);

  return (
    <div className="space-y-5">
      <Tarjeta titulo="Franja horaria" sub="Horario de Argentina (la zona del negocio). Fuera de la franja, la campaña espera sola.">
        {f.config.franjasTexto && <p className="text-sm text-slate-600 mb-3">Hoy: <span className="font-medium text-slate-800">{f.config.franjasTexto}</span></p>}
        <EditorFranjas franjas={e.franjas} onChange={(fr) => f.set("franjas", fr)} errores={erroresFranjas} />
      </Tarjeta>

      <Tarjeta titulo="Ritmo y límites">
        <div className="space-y-5">
          <div>
            <CampoNumero label="Mensajes por segundo" value={e.ritmoMps} onChange={(n) => f.set("ritmoMps", n as number)} min={1} max={80}
              error={f.errores.ritmoMps} ayuda="Entre 1 y 80. Por defecto, 10." />
            {ritmoBajado && (
              <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700">
                <AlertTriangle size={13} className="flex-shrink-0 mt-px" />
                Ahora está en {num(cuenta.ritmoActualMps)}/s: el proveedor de WhatsApp pidió bajar el ritmo. Vuelve solo en una hora.
              </p>
            )}
          </div>

          <fieldset>
            <legend className="text-sm font-medium text-slate-700">Límite diario</legend>
            <div className="mt-2 space-y-2">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="radio" name="cupo" checked={!cupoManual} onChange={() => f.set("cupoManual24h", null)} className="text-violet-600 focus:ring-violet-500" />
                Usar el límite de Meta ({limiteMeta})
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 flex-wrap">
                <input type="radio" name="cupo" checked={cupoManual} onChange={() => f.set("cupoManual24h", NaN)} className="text-violet-600 focus:ring-violet-500" />
                Un límite menor
              </label>
              {cupoManual && (
                <div className="pl-6">
                  <CampoNumero label="Personas cada 24 h" value={e.cupoManual24h} onChange={(n) => f.set("cupoManual24h", n === null ? NaN : n)} min={1}
                    error={f.errores.cupoManual24h} />
                </div>
              )}
              <p className="text-xs text-slate-400">Nunca se puede pasar el de Meta.</p>
            </div>
          </fieldset>
          {!sinNumero && <LimiteWhatsAppManager cuenta={cuenta} recargar={recargar} />}

          <ReglaFatiga f={f} />
          {/* Un backend anterior no los manda: sin el campo no se ofrece (el PATCH lo rechazaría). */}
          {e.frenoPorCalidad !== undefined && (
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={e.frenoPorCalidad} onChange={(ev) => f.set("frenoPorCalidad", ev.target.checked)}
                className="mt-0.5 rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
              <span>
                Frenar solo si Meta baja la calidad del número (recomendado): con calidad media, las campañas salen a la mitad del cupo;
                con calidad baja, el marketing espera 7 días desde la última lectura en Baja.
              </span>
            </label>
          )}
          {e.diasCaliente !== undefined && (
            <CampoNumero label="Primero a quien compró en los últimos" value={e.diasCaliente} onChange={(n) => f.set("diasCaliente", n as number)}
              min={0} max={365} sufijo="días" error={f.errores.diasCaliente}
              ayuda="Junto con los que ya te escribieron, son los «calientes»: con el límite de Meta les llega primero, porque son los que menos bloquean. 0 = sólo los que escribieron." />
          )}
          {/* Sin grupo de control: WhatsApp oficial le escribe a todos los que pasan el chequeo. */}
        </div>
      </Tarjeta>

      <Tarjeta titulo="Teléfonos y idioma">
        <div className="space-y-5">
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={e.excluirTelefonosAmbiguos} onChange={(ev) => f.set("excluirTelefonosAmbiguos", ev.target.checked)}
              className="mt-0.5 rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
            {/* Decía «recomendado hasta confirmar con el piloto»: una nota interna de Deenex que el dueño no tiene cómo entender. */}
            <span>Excluir los números que vinieron sin +54 (recomendado: sin el código de país no sabemos si son celulares)</span>
          </label>

          <div>
            <label htmlFor="wa-idioma" className="text-sm font-medium text-slate-700">Idioma de las plantillas</label>
            <select id="wa-idioma" value={e.idioma} onChange={(ev) => f.set("idioma", ev.target.value)}
              className="mt-1 block rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500">
              <option value="es">Español</option>
              <option value="es_AR">Español de Argentina</option>
              {!["es", "es_AR"].includes(e.idioma) && <option value={e.idioma}>{e.idioma}</option>}
            </select>
            {f.errores.idioma
              ? <p className="text-xs text-red-600 mt-1">{f.errores.idioma}</p>
              : <p className="text-xs text-slate-400 mt-1">Dejalo en Español salvo que Deenex te diga otra cosa. Se aplica a las plantillas nuevas.</p>}
          </div>
        </div>
      </Tarjeta>

      <BotoneraGuardar f={f} />
    </div>
  );
}
